import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Checkbox, Form, Upload, Image, Input } from 'antd';
import LoadableButton from "../../../../../components/buttons/LoadableButton.jsx";
import { DepositAmountApi, getBankDataApi } from '../../../../../api/request/wallet/cashdeposit.js';
import UploadDoc from '../../../../../assets/UploadDoc.jsx';
// tncFileUrl is passed as a prop
import { handleApiError } from '../../../../../utils/handleApiError.js';
import { getDepositAmountError } from '../../../../../utils/validateDepositAmount.js';
import ActiveBankDetails from './ActiveBankDetails.jsx';

const CashDepositMobile = ({ amount, resetAll, tncFileUrl, loginTerminalId, validateAmount, paymentMethod }) => {
    const [form] = Form.useForm();
    const { userDetails } = useSelector(state => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [previewImage, setPreviewImage] = useState('');
    const [bankData, setBankData] = useState([])
    const [signatureFileList, setSignatureFileList] = useState([]);
    const [proofFileList, setProofFileList] = useState([]);
    const needsProofOfFund = Number(amount) > 1000;

    const getBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

    const extractOriginFileObjs = (fileArray) => {
        return fileArray ? fileArray.map((file) => file.originFileObj) : [];
    };

    const handleSubmit = async (values) => {
        setIsLoading(true);
        try {
            const amountError = validateAmount || getDepositAmountError(amount, paymentMethod);
            if (amountError) {
                toast.error(amountError);
                return;
            }
            const UploadDocument = extractOriginFileObjs(values?.UploadDocument);
            const proofOfFund = extractOriginFileObjs(values?.ProofOfFund);
            if (needsProofOfFund && proofOfFund.length === 0) {
                toast.error('Please upload Source of fund.');
                return;
            }
            const isWallet = loginTerminalId === 'wallet';
            let formData = new FormData();
            formData.append("paymentGatewayId", paymentMethod?.id);
            formData.append("UserId", userDetails?._id);
            formData.append("ReferenceId", values?.ReferenceId);
            formData.append("Comment", values?.Comment || "");
            formData.append("Amount", Number(amount).toFixed(2));
            formData.append("DepositType", 3);
            formData.append("loginTerminalId", isWallet ? 0 : loginTerminalId);
            UploadDocument.forEach((file) => formData.append("PaymentProof", file));
            if (needsProofOfFund && proofOfFund[0]) formData.append("ProofOfFund", proofOfFund[0]);

            let apiHeaders = { headers: { Accept: "application/json", "Content-Type": "multipart/form-data" } };

            const { data } = await DepositAmountApi(formData, apiHeaders);
            if (data?.success) {
                toast.success(data?.message);
                setSignatureFileList([]);
                setProofFileList([]);
                form.resetFields();
                resetAll();
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsLoading(false);
        }
    }

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    const handleSignatureChange = ({ fileList: newSignatureList }) => {
        if (newSignatureList.length > 2) {
            setMessage("You can only upload up to 2 files.");
            return;
        }
        setSignatureFileList(newSignatureList);
        form.setFieldsValue({ UploadDocument: newSignatureList });
        form.setFields([{ name: 'UploadDocument', errors: null }]);
        setMessage('');
    };

    const handleRemoveFile = (uid) => {
        const newList = signatureFileList.filter((file) => file.uid !== uid);
        setSignatureFileList(newList);
        form.setFieldsValue({ UploadDocument: newList });
        setMessage('');
    };

    const UploadButton = (
        <button type="button">
            <div className='flex items-center justify-center gap-2 border rounded-md py-1.5 px-4 text-lg text-gray-500 font-medium'>
                <img src="/assets/icons/Upload_Document_icon.svg" alt="icon" className='h-5' />
                <span>Click to Upload</span>
            </div>
        </button>
    );

    const getBankDetails = async () => {
        try {
            const { data } = await getBankDataApi()
            setBankData(Array.isArray(data?.data) ? data.data : []);
        } catch (err) { }
    }

    const handleProofChange = ({ fileList: newProofList }) => {
        setProofFileList(newProofList.slice(-1));
        form.setFieldsValue({ ProofOfFund: newProofList.slice(-1) });
    };

    useEffect(() => {
        getBankDetails()
    }, [])

    useEffect(() => {
        if (!needsProofOfFund) {
            setProofFileList([]);
            form.setFieldsValue({ ProofOfFund: [] });
        }
    }, [needsProofOfFund, form]);


    return (
        <div>
            <Form form={form} layout="vertical" className='space-y-5 mb-6' onFinish={handleSubmit}>
                {/* Bank Details Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    <h3 className="text-lg font-semibold text-blue-600 pb-2 mb-4 border-b border-gray-200">Bank Details</h3>
                    <ActiveBankDetails banks={bankData} />
                </div>

                <Form.Item
                    name="ReferenceId"
                    label={<span className="text-sm font-bold text-gray-700">Reference Id</span>}
                    rules={[{ required: true, message: 'Please enter reference id.' }]}
                >
                    <Input placeholder="Reference Id" size="large" className="py-3" />
                </Form.Item>

                {/* Document Upload Section */}
                <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                        Upload Document <span className="text-red-500">*</span>
                    </label>

                    {signatureFileList.length > 0 && (
                        <div className="space-y-3 mb-4">
                            {signatureFileList.map((file) => (
                                <div
                                    key={file.uid}
                                    className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl"
                                >
                                    {file.originFileObj?.type?.includes('image') ? (
                                        <img
                                            src={URL.createObjectURL(file.originFileObj)}
                                            alt={file.name}
                                            className="w-12 h-12 object-cover rounded-lg shrink-0"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center text-xs font-medium text-gray-600 shrink-0">
                                            PDF
                                        </div>
                                    )}
                                    <span className="flex-1 text-sm text-gray-700 truncate">{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveFile(file.uid)}
                                        className="shrink-0 p-2 rounded-lg bg-[#FFE2E3] text-red-500"
                                        aria-label="Remove file"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                            <path fillRule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 0 1 3.878.512.75.75 0 1 1-.256 1.478l-.209-.035-1.005 13.07a3 3 0 0 1-2.991 2.77H8.084a3 3 0 0 1-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 0 1-.256-1.478A48.567 48.567 0 0 1 7.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 0 1 3.369 0c1.603.051 2.815 1.387 2.815 2.951Zm-6.136-1.452a51.196 51.196 0 0 1 3.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 0 0-6 0v-.113c0-.794.609-1.428 1.364-1.452Zm-.355 5.945a.75.75 0 1 0-1.5.059l.375 9a.75.75 0 1 0 1.5-.059l-.375-9Zm8.611 0a.75.75 0 1 0-1.5.059l.375 9a.75.75 0 1 0 1.5-.059l-.375-9Z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <Form.Item
                        name="UploadDocument"
                        rules={[{ required: true, message: 'Please upload at least one document!' }]}
                        className="mb-0 w-full custom-form-item"
                    >
                        <Upload.Dragger
                            name="files"
                            multiple
                            maxCount={2}
                            showUploadList={false}
                            accept=".pdf,.jpg,.jpeg,.png"
                            fileList={signatureFileList}
                            onChange={handleSignatureChange}
                            beforeUpload={() => false}
                            onPreview={handlePreview}
                            className={`upload-dragger-custom w-full ${signatureFileList.length >= 2 ? 'hidden' : ''}`}
                            style={{
                                borderRadius: '16px',
                                border: '2px dashed #d1d5db',
                                backgroundColor: '#f9fafb'
                            }}
                        >
                            <div className="p-6 text-center">
                                <div className="flex justify-center mb-4">
                                    <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center">
                                        <UploadDoc color="white" />
                                    </div>
                                </div>
                                <h3 className="text-lg font-medium text-gray-700 mb-2">
                                    Upload your Document
                                </h3>
                                <p className="text-sm text-gray-500">
                                    Click or drag files to upload
                                </p>
                            </div>
                        </Upload.Dragger>
                    </Form.Item>
                    {message && (
                        <p className="text-red-500 text-sm mt-2">
                            {message}
                        </p>
                    )}
                </div>

                {needsProofOfFund && (
                    <div className="mb-4">
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                            Source of fund <span className="text-red-500">*</span>
                        </label>
                        <Form.Item
                            name="ProofOfFund"
                            rules={[{ required: true, message: 'Please upload Source of fund.' }]}
                            className="mb-0 w-full custom-form-item"
                        >
                            <Upload
                                beforeUpload={() => false}
                                listType="picture"
                                maxCount={1}
                                accept=".pdf,.jpg,.jpeg,.png"
                                fileList={proofFileList}
                                onChange={handleProofChange}
                                onPreview={handlePreview}
                            >
                                {proofFileList.length >= 1 ? null : UploadButton}
                            </Upload>
                        </Form.Item>
                    </div>
                )}

                {/* Image Preview Modal */}
                {
                    previewImage && (
                        <Image
                            wrapperStyle={{ display: 'none' }}
                            preview={{
                                visible: previewOpen,
                                onVisibleChange: (visible) => setPreviewOpen(visible),
                                afterOpenChange: (visible) => !visible && setPreviewImage(''),
                            }}
                            src={previewImage}
                        />
                    )
                }

                <Form.Item
                    name='conditions'
                    valuePropName="checked"
                    className="mb-2"
                    rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions.')) }]}
                >
                    <Checkbox className="items-start">
                        <span className="text-sm text-gray-700">
                            Yes, I agreed to the{' '}
                            {tncFileUrl && (
                                <a href={tncFileUrl} target="_blank" rel="noopener noreferrer" className="primary underline hover:underline duration-300">Terms & Conditions</a>
                            )}
                        </span>
                    </Checkbox>
                </Form.Item>

                <LoadableButton
                    className='bg-primary text-sm text-white font-semibold rounded-lg px-12 py-3 mt-4 w-full uppercase themeHover duration-500'
                    type="Submit"
                    lable='Submit'
                    isLoading={isLoading}
                    loadingLable='Adding request...'
                />
            </Form>
        </div >
    )
}

export default CashDepositMobile
